/**
 * Controls + Requirements CSV / JSON import + export helpers.
 * Mirror of step7-goals/goalsClaimsCsv.ts.
 */
import { CONTROL_COLUMNS_READ, type ControlRecord } from './controlsFieldSchema';
import { REQUIREMENT_COLUMNS_READ, type RequirementRecord } from './requirementsFieldSchema';

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

function buildCsv(columns: { key: string; label: string }[], rows: Record<string, unknown>[]): string {
  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((r) => columns.map((c) => escapeCsv(r[c.key])).join(','));
  return [header, ...body].join('\n');
}

function triggerDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function exportControlsToCsv(controls: ControlRecord[]): void {
  const csv = buildCsv(CONTROL_COLUMNS_READ, controls as Record<string, unknown>[]);
  triggerDownload(csv, `cybersecurity_controls_${new Date().toISOString().split('T')[0]}.csv`);
}

export function exportRequirementsToCsv(reqs: RequirementRecord[]): void {
  const csv = buildCsv(REQUIREMENT_COLUMNS_READ, reqs as Record<string, unknown>[]);
  triggerDownload(csv, `cybersecurity_requirements_${new Date().toISOString().split('T')[0]}.csv`);
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cell += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell); cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

const CONTROL_HEADER_OVERRIDES: Record<string, string> = {
  id: 'control_id',
  name: 'control_name',
  description: 'control_description',
  type: 'control_type',
  parent_goal: 'goal_id',
};

const REQUIREMENT_HEADER_OVERRIDES: Record<string, string> = {
  id: 'req_id',
  title: 'req_title',
  statement: 'req_statement',
  rationale: 'req_rationale',
  type: 'requirement_type',
  control: 'control_id',
  cal: 'cal_level',
};

function headerToKey(
  header: string,
  columns: { key: string; label: string }[],
  overrides: Record<string, string>,
): string {
  const col = columns.find((c) => c.label.toLowerCase() === header.trim().toLowerCase());
  if (col) return col.key;
  const slug = header.trim().toLowerCase().replace(/\s+/g, '_');
  return overrides[slug] ?? slug;
}

function parseCsvWithSchema<T>(
  text: string,
  columns: { key: string; label: string }[],
  overrides: Record<string, string>,
): T[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => headerToKey(h, columns, overrides));
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => { out[key] = row[i] ?? ''; });
    return out as T;
  });
}

export function parseControlsCsv(text: string): ControlRecord[] {
  return parseCsvWithSchema<ControlRecord>(text, CONTROL_COLUMNS_READ, CONTROL_HEADER_OVERRIDES);
}

export function parseRequirementsCsv(text: string): RequirementRecord[] {
  return parseCsvWithSchema<RequirementRecord>(text, REQUIREMENT_COLUMNS_READ, REQUIREMENT_HEADER_OVERRIDES);
}

export function parseControlsJson(text: string): ControlRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as ControlRecord[];
  if (parsed && Array.isArray(parsed.cybersecurity_controls)) return parsed.cybersecurity_controls as ControlRecord[];
  if (parsed && Array.isArray(parsed.controls)) return parsed.controls as ControlRecord[];
  throw new Error('JSON must be an array of controls or `{ cybersecurity_controls: [...] }`');
}

export function parseRequirementsJson(text: string): RequirementRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as RequirementRecord[];
  if (parsed && Array.isArray(parsed.cybersecurity_requirements)) return parsed.cybersecurity_requirements as RequirementRecord[];
  if (parsed && Array.isArray(parsed.requirements)) return parsed.requirements as RequirementRecord[];
  throw new Error('JSON must be an array of requirements or `{ cybersecurity_requirements: [...] }`');
}

export async function parseControlsFile(file: File): Promise<ControlRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseControlsJson(text);
  if (ext === 'csv') return parseControlsCsv(text);
  try { return parseControlsJson(text); } catch { return parseControlsCsv(text); }
}

export async function parseRequirementsFile(file: File): Promise<RequirementRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseRequirementsJson(text);
  if (ext === 'csv') return parseRequirementsCsv(text);
  try { return parseRequirementsJson(text); } catch { return parseRequirementsCsv(text); }
}
